import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { app } from '../config/firebase';
import { notificationsApi } from './api';

let messaging: Messaging | null = null;

// Initialize messaging only in browser environment
if (typeof window !== 'undefined') {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.error('Error initializing Firebase Messaging:', error);
  }
}

/**
 * Request notification permission and get FCM token
 * @returns FCM token if permission granted, null otherwise
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  if (!messaging) {
    console.error('Firebase Messaging not initialized');
    return null;
  }

  try {
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted.');
      
      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
      });
      
      if (token) {
        console.log('FCM Token:', token);
        
        // Subscribe to backend topic
        try {
          await notificationsApi.subscribe(token);
          console.log('Successfully subscribed to backend notifications');
        } catch (err) {
          console.error('Error subscribing to backend notifications:', err);
          // We don't block the flow if backend sub fails, but we log it
        }

        return token;
      } else {
        console.log('No registration token available.');
        return null;
      }
    } else {
      console.log('Notification permission denied.');
      return null;
    }
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

/**
 * Listen for foreground messages
 * @returns Promise that resolves with message payload
 */
export const onMessageListener = (): Promise<any> => {
  if (!messaging) {
    return Promise.reject('Firebase Messaging not initialized');
  }

  return new Promise((resolve) => {
    onMessage(messaging!, (payload) => {
      console.log('Foreground message received:', payload);
      resolve(payload);
    });
  });
};

/**
 * Get current FCM token without requesting permission
 * @returns FCM token if available, null otherwise
 */
export const getCurrentToken = async (): Promise<string | null> => {
  if (!messaging) {
    return null;
  }

  try {
    if (Notification.permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
      });

      if (token) {
        // Ensure backend subscription is active/refreshed
        // We do this silently without blocking return
        notificationsApi.subscribe(token).catch(err => 
          console.error('Error refreshing backend subscription:', err)
        );
        return token;
      }
      return null;
    }
    return null;
  } catch (error) {
    console.error('Error getting current token:', error);
    return null;
  }
};
